import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import AttachmentIcon from '../Attachments/AttachmentIcon';
import UploadButton from '../Attachments/UploadButton';
import styles from './MyHistory.module.css';

const STATUS_LABEL = { Open: 'ממתין', Executed: 'בוצע', Rejected: 'נדחה' };

export default function MyHistoryPage({ user }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getPaymentRequests(user.credential);
      setRequests(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleDelete = async (id) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את הבקשה? פעולה זו אינה הפיכה.')) return;
    try {
      await api.deletePaymentRequest(user.credential, id);
      load();
    } catch (e) {
      alert('מחיקת הבקשה נכשלה');
    }
  };

  const filtered = statusFilter
    ? requests.filter(r => r.status === statusFilter)
    : requests;

  const totalAmount = filtered.reduce((s, r) => s + r.amount, 0);
  const formatDate = (iso) => new Date(iso).toLocaleDateString('he-IL');
  const formatAmount = (n) => `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 0 })}`;

  return (
    <div className={styles.container}>
      <h2>הבקשות שלי</h2>

      {/* Stats */}
      {!loading && requests.length > 0 && (
        <div className={styles.statsRow}>
          <div className={styles.stat}>
            <span className={styles.statValue}>{requests.length}</span>
            <span className={styles.statLabel}>בקשות</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{formatAmount(totalAmount)}</span>
            <span className={styles.statLabel}>סה"כ מסונן</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{requests.filter(r => r.status === 'Open').length}</span>
            <span className={styles.statLabel}>ממתינות</span>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className={styles.filters}>
        <button className={`${styles.filterBtn} ${!statusFilter ? styles.filterActive : ''}`} onClick={() => setStatusFilter('')}>הכל</button>
        <button className={`${styles.filterBtn} ${statusFilter === 'Open' ? styles.filterActive : ''}`} onClick={() => setStatusFilter('Open')}>ממתין</button>
        <button className={`${styles.filterBtn} ${statusFilter === 'Executed' ? styles.filterActive : ''}`} onClick={() => setStatusFilter('Executed')}>בוצע</button>
        <button className={`${styles.filterBtn} ${statusFilter === 'Rejected' ? styles.filterActive : ''}`} onClick={() => setStatusFilter('Rejected')}>נדחה</button>
      </div>

      {loading && <p className={styles.empty}>טוען...</p>}
      {!loading && filtered.length === 0 && <p className={styles.empty}>אין בקשות.</p>}

      {/* Cards for mobile-friendly display */}
      <div className={styles.list}>
        {filtered.map((r) => (
          <div key={r.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardDate}>{formatDate(r.createdAt)}</span>
              <span className={`${styles.badge} ${styles['badge' + r.status]}`}>
                {STATUS_LABEL[r.status]}
              </span>
              <AttachmentIcon paymentRequestId={r.id} hasAttachments={r.hasAttachments} user={user} canDelete={r.status === 'Open'} />
            </div>
            <div className={styles.cardBody}>
              <div className={styles.cardDesc}>{r.description}</div>
              {r.bankAccountSnapshot && <div className={styles.cardBank}>{r.bankAccountSnapshot}</div>}
            </div>
            <div className={styles.cardFooter}>
              <span className={styles.cardAmount}>{formatAmount(r.amount)}</span>
              <div className={styles.cardActions}>
                {/* Upload is allowed in any status */}
                <UploadButton paymentRequestId={r.id} user={user} onUploaded={load} className={styles.uploadBtn} />
                {r.status === 'Open' && (
                  <button className={styles.editBtn} onClick={() => navigate(`/requests?edit=${r.id}`)}>ערוך</button>
                )}
                {r.status === 'Open' && (
                  <button className={styles.deleteBtn} onClick={() => handleDelete(r.id)}>מחק</button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
